import { Modal, Status, Table } from '@bitcine/cinesend-theme/dist'
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import axios from 'axios'
import useTableFilters from '../../hooks/use_table_filters'
import { useSnackbar } from '../../hooks/use_snackbar'
import { useParams } from 'react-router-dom'

const AddModal = ({ onClose, type, queryKey, category, invalidateKey, langID }) => {
  const { addMessage } = useSnackbar()
  const [selectedIDs, setSelectedIDs] = useState([])
  const { data: items, isLoading, isError, error } = useQuery(queryKey)
  const { cycleID } = useParams()
  const { data: cycle } = useQuery([`cycles/${cycleID}`])
  const {
    data: ratingCollection,
    isLoading: isRatingCollectionLoading,
    isError: isRatingCollectionError,
    error: ratingCollectionError,
  } = useQuery({
    queryKey: [`/collections?schema_id=${cycle?.schema_id}&search=Ratings`],
    enabled: cycle?.id !== null,
  })
  const ratingCollectionId = ratingCollection?.[0]?.id
  const {
    data: ratings,
    isLoading: isRatingsLoading,
    isError: isRatingError,
    error: ratingError,
  } = useQuery({
    queryKey: [`collection-values?collectionID=${ratingCollectionId}`],
    enabled: ratingCollectionId !== null,
  })
  const title = ['assets', 'warning-slates', 'ad-pools', 'interstitial'].includes(type) ? 'Assets' : 'Categories'
  const queryClient = useQueryClient()

  const showWarningSlateAssetFilters = type === 'warning-slates' && !queryKey?.[0].includes('&adAssignment')
  const filteringOptions = showWarningSlateAssetFilters
    ? [
        {
          label: 'Rating',
          key: 'rating',
          type: 'radio_buttons',
          options: ratings?.map((rating) => ({ label: rating.label, key: rating.id, value: rating.id })) ?? [],
        },
      ]
    : null

  const { filtering, searching } = useTableFilters({ filteringOptions, enableSearch: true })

  const filteredItems = items
    ?.filter((item) => {
      if (searching.search) {
        const search = searching.search.toLowerCase()
        return (
          item.title?.toLowerCase().includes(search) ||
          item.category_values?.some((v) => v.value?.toLowerCase().includes(search))
        )
      }
      return true
    })
    .filter((item) => {
      if (filtering?.filters?.rating) {
        return item.field_values?.[0]?.value === filtering.filters.rating.toString()
      }
      return true
    })

  const isFeaturedContent = queryKey?.[0].includes('&featuredContentAssignment')
  const addItems = useMutation(
    () => {
      return axios.post(`/categories/${category.id}/add-items`, {
        type,
        ids: selectedIDs,
        isFeaturedContent,
      })
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(invalidateKey)
        onClose()
      },
      onError: (err) => {
        addMessage(err?.response?.data?.error ?? err.message, 'error')
      },
    }
  )

  const toggleSelectedID = (id) => {
    selectedIDs.includes(id)
      ? setSelectedIDs(selectedIDs.filter((i) => i !== id))
      : setSelectedIDs([...selectedIDs, id])
  }

  return (
    <Status
      pending={isRatingsLoading || isRatingCollectionLoading}
      isError={isRatingCollectionError || isRatingError}
      errorMessage={ratingCollectionError ?? ratingError}
    >
      <Modal
        header={`Add ${title}`}
        onClose={onClose}
        confirmButton={{
          text: 'Add',
          disabled: selectedIDs.length === 0,
          onClick: () => addItems.mutate(),
        }}
      >
        <div className={'max-h-96 overflow-y-scroll'}>
          <Table
            status={{
              pending: isLoading,
              error: isError,
              errorMessage: error?.message,
            }}
            header={{
              columns: [
                { text: 'Name', key: 'name' },
                showWarningSlateAssetFilters
                  ? { text: 'Rating', key: 'plural' }
                  : { text: 'Asset Type', key: 'plural' },
              ],
              searching,
              filtering,
              checkbox: {
                checked: items && items.length > 0 && selectedIDs.length === items.length,
                indeterminate: items && selectedIDs.length > 0 && selectedIDs.length < items.length,
                onChange: () => {
                  if (selectedIDs.length === items.length) {
                    setSelectedIDs([])
                  } else {
                    setSelectedIDs(items.map(({ id }) => id))
                  }
                },
              },
            }}
            widths={['auto', '85']}
            body={{
              data: filteredItems,
              row: {
                compact: true,
                spaced: true,
                onClick: (event, data) => toggleSelectedID(data.id),
                checkbox: {
                  checked: (data) => selectedIDs.includes(data.id),
                  onChange: (data) => toggleSelectedID(data.id),
                },
                render: [
                  (data, index, { langID: langID }) =>
                    data.title ?? data.category_values?.find((v) => v.lang_id === langID)?.value,
                  (data) =>
                    showWarningSlateAssetFilters
                      ? ratings?.find((r) => r.id == data.field_values?.[0]?.value)?.label
                      : data.plural_label,
                ],
              },
              empty: {
                title: `No ${title.toLowerCase()} found!`,
                text: `Create ${title.toLowerCase()} to begin.`,
                icon: 'article',
              },
            }}
          />
        </div>
      </Modal>
    </Status>
  )
}

export default AddModal
